import {
  Alert,
  Anchor,
  Button,
  Card,
  Grid,
  Group,
  Loader,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  VisuallyHidden,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconAlertTriangle, IconCircleCheck, IconMail } from "@tabler/icons-react";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import { semanticColor } from "@how/design-system";
import { LoadingState, PageHeader, PermissionDenied, timeZoneOptions } from "@how/ui";
import { z } from "zod";
import { env } from "../../lib/env";
import { usePlatformRole } from "../auth/platform-role";
import { PLAN_KEYS, PLAN_LABEL, usePlans } from "../plans/plans";
import {
  SLUG_PATTERN,
  describeCreateError,
  useCreateChurch,
  useSlugStatus,
  type CreatedChurch,
  type SlugStatus,
} from "./church-owners";

const formSchema = z.object({
  name: z.string().trim().min(1, "Enter the church's name").max(200, "Up to 200 characters"),
  slug: z
    .string()
    .trim()
    .toLowerCase()
    .regex(
      SLUG_PATTERN,
      "Use 2 to 63 lowercase letters, digits or hyphens, starting and ending with a letter or digit",
    ),
  plan: z.enum(["core", "pro", "elite"]),
  timeZone: z.string().min(1, "Choose a time zone"),
  countryCode: z
    .string()
    .trim()
    .regex(/^[A-Za-z]{2}$/, "A two-letter country code, such as US"),
  locale: z
    .string()
    .trim()
    .regex(/^[a-z]{2,3}(-[A-Za-z0-9]{2,8})*$/, "A language code, such as en-US"),
  ownerEmail: z.email("Enter the owner's email address").trim(),
});
type FormValues = z.input<typeof formSchema>;

/** "Grace Church of Springfield" → "grace-church-of-springfield", within the address rule's 63 characters. */
export function slugFromName(name: string): string {
  return name
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 63)
    .replace(/-+$/g, "");
}

/** The words under the address field for the database's answer. */
export function slugStatusMessage(status: SlugStatus, slug: string): string | null {
  switch (status) {
    case "reserved":
      return `${slug} is reserved for HoW's own addresses. Choose another.`;
    case "taken":
      return `${slug} is already taken by another church. Choose another.`;
    case "invalid":
      return "Use 2 to 63 lowercase letters, digits or hyphens, starting and ending with a letter or digit";
    case "available":
      return null;
  }
}

/**
 * Create a church (ADR 0064): its name, address, plan, time zone, country and language, and its first owner's email.
 * A platform super admin's page; DCX support has no button for it and the database refuses them. The address is
 * checked against the database as it is typed. On submit the church is created in one transaction; an owner with a
 * HoW login becomes the owner at once, a new address is sent an invitation.
 */
export function CreateChurchPage() {
  const role = usePlatformRole();
  const [created, setCreated] = useState<CreatedChurch | null>(null);
  if (role.isPending) return <LoadingState rows={6} />;
  if (role.data !== "super_admin") {
    return (
      <PermissionDenied
        title="Only a super admin creates churches"
        message="Ask a DCX Logic super admin to create the church and invite its owner."
      />
    );
  }
  return (
    <>
      <PageHeader
        title="Create a church"
        description="A new church with its three addresses, the base modules and its plan, and its first owner."
        above={
          <Anchor component={Link} to="/tenants" size="sm">
            Back to tenants
          </Anchor>
        }
      />
      {created === null ? (
        <CreateChurchForm onCreated={setCreated} />
      ) : (
        <CreatedChurchCard
          created={created}
          onAnother={() => {
            setCreated(null);
          }}
        />
      )}
    </>
  );
}

function CreateChurchForm({ onCreated }: { readonly onCreated: (c: CreatedChurch) => void }) {
  const plans = usePlans();
  const create = useCreateChurch();
  const [error, setError] = useState<string | null>(null);
  /** The address follows the name until someone types in it. */
  const slugTouched = useRef(false);
  const zones = useMemo(() => timeZoneOptions(new Date(), ["America/New_York"]), []);
  const form = useForm<FormValues>({
    initialValues: {
      name: "",
      slug: "",
      plan: "core",
      timeZone: "America/New_York",
      countryCode: "US",
      locale: "en-US",
      ownerEmail: "",
    },
    validate: zod4Resolver(formSchema),
  });
  const slug = form.values.slug.trim().toLowerCase();
  const check = useSlugStatus(slug);
  const slugError =
    form.errors.slug ??
    (check.status !== null && check.status !== "available"
      ? slugStatusMessage(check.status, slug)
      : null);
  const base = env.VITE_HOW_PLATFORM_BASE_DOMAIN;
  const planOptions =
    plans.data?.map((p) => ({ value: p.key, label: p.name })) ??
    PLAN_KEYS.map((key) => ({ value: key, label: PLAN_LABEL[key] }));

  const submit = (values: FormValues): void => {
    setError(null);
    // A refused address stops here; one still being checked, or whose check failed, goes to the database, which
    // checks it again and answers 23514 or 23505.
    if (check.status !== null && check.status !== "available") {
      form.setFieldError("slug", slugStatusMessage(check.status, slug));
      return;
    }
    create.mutate(
      {
        name: values.name,
        slug: values.slug,
        plan: values.plan,
        timeZone: values.timeZone,
        countryCode: values.countryCode,
        locale: values.locale,
        ownerEmail: values.ownerEmail,
      },
      {
        onSuccess: (made) => {
          notifications.show({
            color: semanticColor("success"),
            title: "Church created",
            message: `${made.name} is ready at ${made.hosts.site}.`,
          });
          onCreated(made);
        },
        onError: (failure) => {
          const words = describeCreateError(failure);
          setError(words);
          notifications.show({
            color: semanticColor("danger"),
            title: "The church was not created",
            message: words,
          });
        },
      },
    );
  };

  return (
    <Card maw={760}>
      <form
        onSubmit={form.onSubmit(submit)}
        aria-label="Create a church"
        noValidate
        data-testid="create-church-form"
      >
        <Stack gap="md">
          <Title order={2} size="h4">
            The church
          </Title>
          <TextInput
            label="Church name"
            placeholder="Grace Church"
            required
            {...form.getInputProps("name")}
            onChange={(event) => {
              const value = event.currentTarget.value;
              form.setFieldValue("name", value);
              if (!slugTouched.current) form.setFieldValue("slug", slugFromName(value));
            }}
          />
          <TextInput
            label="Address"
            required
            placeholder="grace"
            description={
              slug.length > 0
                ? `Website ${slug}.${base}, staff dashboard ${slug}.dash.${base}, members ${slug}.members.${base}. It cannot be changed later.`
                : "The church's name in its addresses: lowercase letters, digits and hyphens. It cannot be changed later."
            }
            {...form.getInputProps("slug")}
            error={slugError}
            onChange={(event) => {
              const value = event.currentTarget.value;
              slugTouched.current = true;
              form.setFieldValue("slug", value);
            }}
            rightSection={check.checking ? <Loader size="xs" aria-label="Checking" /> : null}
          />
          <Text size="sm" role="status" aria-live="polite" c="dimmed" mih={20}>
            {check.checking
              ? "Checking the address…"
              : check.status === "available"
                ? `${slug}.${base} is available.`
                : check.failed
                  ? "The address could not be checked. It is checked again when you create the church."
                  : ""}
          </Text>
          <Grid gutter="md">
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Select
                label="Plan"
                required
                allowDeselect={false}
                data={planOptions}
                {...form.getInputProps("plan")}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Select
                label="Time zone"
                required
                searchable
                allowDeselect={false}
                nothingFoundMessage="No zone by that name"
                data={zones}
                {...form.getInputProps("timeZone")}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <TextInput
                label="Country"
                description="Two letters, such as US or CA"
                required
                maxLength={2}
                {...form.getInputProps("countryCode")}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <TextInput
                label="Language"
                description="Such as en-US or es-US"
                required
                {...form.getInputProps("locale")}
              />
            </Grid.Col>
          </Grid>
          <Title order={2} size="h4" mt="sm">
            The first owner
          </Title>
          <TextInput
            label="Owner's email"
            type="email"
            required
            placeholder="pastor@gracechurch.org"
            description="If this address already has a HoW login, they become the church's owner at once. If not, they are sent an invitation email to set up their login."
            {...form.getInputProps("ownerEmail")}
          />
          {error === null ? null : (
            <Alert
              color={semanticColor("danger")}
              variant="light"
              icon={<IconAlertTriangle size={18} />}
              role="alert"
            >
              {error}
            </Alert>
          )}
          <Group justify="flex-end">
            <Button component={Link} to="/tenants" variant="default">
              Cancel
            </Button>
            <Button type="submit" loading={create.isPending}>
              Create church
            </Button>
          </Group>
        </Stack>
      </form>
    </Card>
  );
}

function CreatedChurchCard({
  created,
  onAnother,
}: {
  readonly created: CreatedChurch;
  readonly onAnother: () => void;
}) {
  const rows: readonly [string, string][] = [
    ["Website", created.hosts.site],
    ["Staff dashboard", created.hosts.dash],
    ["Member portal", created.hosts.members],
  ];
  return (
    <Card maw={760}>
      <Stack gap="md">
        <Group gap="xs">
          <IconCircleCheck size={20} aria-hidden />
          <Title order={2} size="h4">
            {created.name} is ready
          </Title>
        </Group>
        <Table.ScrollContainer minWidth={420}>
          <Table aria-label="The church's addresses">
            <Table.Tbody>
              {rows.map(([label, host]) => (
                <Table.Tr key={label}>
                  <Table.Th scope="row" w={180}>
                    {label}
                  </Table.Th>
                  <Table.Td>
                    <Anchor href={`https://${host}`} target="_blank" rel="noreferrer">
                      {host}
                      <VisuallyHidden> (opens in a new tab)</VisuallyHidden>
                    </Anchor>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
        {created.owner.kind === "active" ? (
          <Alert
            color={semanticColor("success")}
            variant="light"
            icon={<IconCircleCheck size={18} />}
            role="status"
          >
            {created.ownerEmail} is the owner. They already had a HoW login, so they can sign in to
            the staff dashboard now.
          </Alert>
        ) : created.owner.kind === "invited" ? (
          <Alert
            color={semanticColor("info")}
            variant="light"
            icon={<IconMail size={18} />}
            role="status"
          >
            Invitation sent to {created.ownerEmail}. They become the owner when they open the email
            and set up their login.
          </Alert>
        ) : (
          <Alert
            color={semanticColor("warning")}
            variant="light"
            icon={<IconAlertTriangle size={18} />}
            role="alert"
            title="The church was created, but the invitation was not sent"
          >
            {created.owner.reason} Open the church and use Invite an owner to try again.
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onAnother}>
            Create another church
          </Button>
          <Button component={Link} to={`/tenants/${created.tenantId}`}>
            Open the church
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}
